import React, { ReactNode, FC, useCallback, useEffect, useMemo, useState, ReactElement, useContext } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import Pagination, { PaginationProps } from 'ia-react-core/~/components/pagination/Pagination';

import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import { SCP } from 'ia-react-core/~/interfaces/SCP';
import Router from 'ia-react-core/~/services/router/Router';
import setPaginationPageByName from '../../../actions/pagination-fn/setPaginationPageByName';
import filterPropsByComponent from '../../../utils/components/_filterPropsByComponent';
import excludeChildrenByType from '../../../utils/components/_excludeChildrenByType';
import findChildByType from '../../../utils/components/_findChildByType';
import ResourceLoader, { ResourceLoaderProps, ResourceLoadRenderProps } from '../ResourceLoader';
import ContentResourceLoader, { ContentResourceLoaderProps } from '../ContentResourceLoader';
import useScopedSelector from '../../../hooks/useScopedSelector';
import { StyledContent, StyledFooter, StyledLeftFooter, StyledMiddleFooter, StyledRightFooter } from './PaginationResourceLoader.styles';
import { Content, FooterLeftContent, FooterMiddleContent } from './PaginationResourceLoader.compounds';
import SchemaContext from '../../schema/_SchemaContext/SchemaContext';

export interface PaginationResourceLoaderProps<Data, Error = {}> extends
  Omit<ContentResourceLoaderProps<Data, Error>, keyof SCP<'aside'>>, PaginationProps {
  /** Content of the resource loader. */
  children?: ReactNode;
  /** Select the active page from a querystring param. */
  selectPageFromUrl?: boolean;
  /** Method used on the "history". Use "push" if you want the navigate through the previous page selection when clicking the browser "back" button. */
  selectPageFromUrlMethod?: 'push' | 'replace';
  /** Name of the querystring param to select the active page from when "selectPageFromUrl" is true. By default, the param name is the "name" of the PaginationResourceLoader. */
  selectPageFromUrlParam?: string;
  /** Reverse the order of the pagination footer, with the pagination displayed to the left. */
  reverseFooter?: boolean;
  /** Align Pagination to the center of the footer. */
  centerFooter?: boolean;
  /** Given the response data, return the item count. */
  getItemCountFromResponse?: (data: any, headers: any) => number;
  /** @ignore */
  offsetParam?: PaginationProps['offsetParam'];
  /** @ignore */
  limitParam?: PaginationProps['limitParam'];
  /** @ignore */
  itemPerPage?: PaginationProps['itemPerPage'];
}

interface PaginationResourceLoaderInternalProps {

}

interface PaginationResourceLoaderComposedProps<Data, Error = {}> extends PaginationResourceLoaderProps<Data, Error>, PaginationResourceLoaderInternalProps {

}

export interface PaginationResourceLoaderMock {}

const PaginationResourceLoaderDefaultProps: Partial<PaginationResourceLoaderProps<any, any>> = {
  offsetParam: 'offset',
  limitParam: 'limit',
  itemPerPage: 10,
  selectPageFromUrl: false,
  selectPageFromUrlMethod: 'push',
  centerFooter: false,
};

export type PaginationResourceLoaderStatics = {
  Content: typeof Content;
  FooterLeftContent: typeof FooterLeftContent;
  FooterMiddleContent: typeof FooterMiddleContent;
};

/**
 * @mode developer
 */
const PaginationResourceLoader = <Data, Error = {}>(props: PaginationResourceLoaderComposedProps<Data, Error>) => {
  const {
    name, itemPerPage, url, children, offsetParam, limitParam, initialCurrentPage, selectPageFromUrl, selectPageFromUrlMethod, selectPageFromUrlParam, reverseFooter, centerFooter, itemCount, getItemCountFromResponse, getDataFromResponse, useLinks,
  } = props;

  const dispatch = useDispatch();
  const history = Router.useHistory();
  const location = Router.useLocation();
  const content = excludeChildrenByType(children, FooterLeftContent, FooterMiddleContent);
  const footerLeftContent = findChildByType(children, FooterLeftContent);
  const footerMiddleContent = findChildByType(children, FooterMiddleContent);
  const [isInitialRender, setIsInitialRender] = useState(true);

  const resolvedContent = useMemo<ResourceLoaderProps<Data, Error>['children']>(() => {
    const firstChild = content[0] as ReactElement<{ children: ReactNode }, FC<{ children: ReactNode }> & { type: Symbol }>;

    if (firstChild?.type?.type === Content.type) {
      return firstChild.props.children;
    } if (content.length === 1) {
      return firstChild;
    }
    return content;
  }, [content]);

  const querystringParam = useMemo(() => selectPageFromUrlParam || name, [name, selectPageFromUrlParam]);

  const resolvedInitialCurrentPage = useMemo(() => {
    if (initialCurrentPage) return initialCurrentPage;
    const { searchParams } = new URL(window.location.href);
    return Number(searchParams.get(querystringParam) || 1);
  }, [initialCurrentPage, querystringParam]);

  const headers = useScopedSelector(`${name}.headers`);

  const { schema } = useContext(SchemaContext);
  const paginationName = useMemo(() => (
    schema.extendName('pagination', { name })
  ), [schema, name]);

  const currentPage: number = useScopedSelector(`${paginationName}.currentPage`) || resolvedInitialCurrentPage;

  const urlWithOrigin = useMemo(() => {
    if (url.startsWith('http')) {
      return url;
    }

    if (url.startsWith('/')) {
      return `${axios.defaults.baseURL || window.location.origin}${url}`;
    }

    return `${axios.defaults.baseURL || window.location.origin}/${url}`;
  }, [url]);

  const urlWithPagination = useMemo(() => {
    const newUrl = new URL(urlWithOrigin);
    const offset = (itemPerPage * currentPage) - itemPerPage;
    const resolvedOffset = !Number.isInteger(offset) ? 0 : offset;

    newUrl.searchParams.set(offsetParam, String(resolvedOffset));
    newUrl.searchParams.set(limitParam, String(itemPerPage));

    return newUrl.toString();
  }, [currentPage, itemPerPage, limitParam, offsetParam, urlWithOrigin]);

  // Handle location change
  useEffect(() => {
    if (!isInitialRender && selectPageFromUrl) {
      const { searchParams } = new URL(window.location.href);
      const selectedPageFromUrl = Number(searchParams.get(querystringParam) || 1);

      if (selectedPageFromUrl !== currentPage) {
        dispatch(setPaginationPageByName(paginationName, selectedPageFromUrl));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectPageFromUrl, querystringParam, dispatch, paginationName, isInitialRender]);

  // Handle pagination change
  useEffect(() => {
    if (!isInitialRender && selectPageFromUrl && currentPage) {
      const { searchParams } = new URL(window.location.href);
      const currentUrlPage = Number(searchParams.get(querystringParam) || 1);
      if (currentPage !== currentUrlPage) {
        searchParams.set(querystringParam, String(currentPage));

        setTimeout(() => {
          history[selectPageFromUrlMethod]({
            ...history.location,
            search: searchParams.toString(),
          });
        });
      }
    }

    setIsInitialRender(false);
  }, [selectPageFromUrl, querystringParam, currentPage, dispatch, paginationName, history, selectPageFromUrlMethod, isInitialRender]);

  // Clear the param on unmount
  useEffect(() => () => {
    if (selectPageFromUrl) {
      const { searchParams } = new URL(window.location.href);
      searchParams.delete(querystringParam);
      history.replace({
        ...history.location,
        search: searchParams.toString(),
      });
    }
  }, [history, querystringParam, selectPageFromUrl]);

  const renderContent = useCallback((renderProps: ResourceLoadRenderProps<Data>) => {
    if (typeof resolvedContent === 'function') {
      return resolvedContent(renderProps);
    }

    if (Array.isArray(resolvedContent)) {
      return resolvedContent.map((child, index) => <React.Fragment key={`${index}`}>{child}</React.Fragment>);
    }

    return resolvedContent;
  }, [resolvedContent]);

  return (
    <ContentResourceLoader<Data, Error>
      {...filterPropsByComponent(props, ContentResourceLoader<Data, Error>)}
      url={urlWithPagination}
      data-testid="PaginationResourceLoader"
      getDataFromResponse={getDataFromResponse}
    >
      {(renderProps) => (
        <>
          <StyledContent>
            {renderContent(renderProps)}
          </StyledContent>
          <StyledFooter reverseFooter={reverseFooter}>
            <StyledLeftFooter>{footerLeftContent || ''}</StyledLeftFooter>
            <StyledMiddleFooter $center={centerFooter}>{footerMiddleContent || ''}</StyledMiddleFooter>
            <StyledRightFooter $center={centerFooter}>
              <Pagination
                offsetParam={offsetParam}
                limitParam={limitParam}
                {...filterPropsByComponent(props, Pagination)}
                name={paginationName}
                initialCurrentPage={resolvedInitialCurrentPage}
                itemCount={(renderProps?.responseData && getItemCountFromResponse?.(renderProps.responseData, headers)) || itemCount}
                useLinks={selectPageFromUrl || useLinks}
              />
            </StyledRightFooter>
          </StyledFooter>
        </>
      )}
    </ContentResourceLoader>
  );
};

PaginationResourceLoader.defaultProps = PaginationResourceLoaderDefaultProps;
PaginationResourceLoader.publicProps = [...ResourceLoader.publicProps, 'limitParam', 'offsetParam', 'selectPageFromUrl', 'selectPageFromUrlMethod', 'selectPageFromUrlParam', 'reverseFooter', 'getItemCountFromResponse', 'getDataFromResponse'] as StringKeyOf<PaginationResourceLoaderProps<any, any>>[];
PaginationResourceLoader.Content = Content;
PaginationResourceLoader.FooterLeftContent = FooterLeftContent;
PaginationResourceLoader.FooterMiddleContent = FooterMiddleContent;

export default PaginationResourceLoader;
